// src/components/DealsCategoriesStrip.jsx
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchFeaturedCategories, slugify } from '../lib/categories'

export default function DealsCategoriesStrip({ limit = 10 }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // 👉 piste scrollable + état des flèches
  const trackRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        setLoading(true)
        setError(null)

        // on récupère les catégories “en vedette”
        const cats = await fetchFeaturedCategories(limit, 0)
        setCategories(cats)
      } catch (e) {
        console.error(e)
        setError(e?.message || 'Erreur de chargement des catégories.')
      } finally {
        setLoading(false)
      }
    })()
  }, [limit])

  // 🔁 met à jour l’état des flèches selon la position de scroll
  const updateArrows = () => {
    const el = trackRef.current
    if (!el) return
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 10)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 10)
  }

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    updateArrows()
    const onScroll = () => updateArrows()
    const onResize = () => updateArrows()

    el.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onResize)

    return () => {
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [categories])

  const scrollTrack = (direction) => {
    const el = trackRef.current
    if (!el) return
    const amount = el.clientWidth * 0.8 // ~80 % de la largeur visible
    const next =
      direction === 'left'
        ? el.scrollLeft - amount
        : el.scrollLeft + amount

    el.scrollTo({ left: next, behavior: 'smooth' })
  }

  if (loading) {
    return (
      <section className="dc-strip my-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Shop deals &amp; more by category</h5>
          <span className="small text-muted">Chargement…</span>
        </div>

        <div className="dc-track-wrapper">
          <div className="dc-track">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="dc-card" key={i}>
                <div className="dc-card-inner skeleton shimmer" />
                <div className="dc-card-label skeleton-text shimmer" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (error || categories.length === 0) return null

  return (
    <section className="dc-strip my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Shop deals &amp; more by category</h5>
        <Link to="/catalog" className="small text-decoration-underline">
          View all
        </Link>
      </div>

      {/* wrapper pour pouvoir afficher les flèches au-dessus */}
      <div className="dc-track-wrapper position-relative">
        {canScrollLeft && (
          <button
            type="button"
            className="dc-arrow dc-arrow-left"
            onClick={() => scrollTrack('left')}
            aria-label="Voir les catégories précédentes"
          >
            ‹
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            className="dc-arrow dc-arrow-right"
            onClick={() => scrollTrack('right')}
            aria-label="Voir les catégories suivantes"
          >
            ›
          </button>
        )}

        <div className="dc-track" ref={trackRef}>
          {categories.map((cat) => {
            const label = cat.label || 'Catégorie'
            const img =
              cat.imageUrl ||
              'https://via.placeholder.com/160x160?text=Image'

            const slug = slugify(label)

            return (
              <Link
                key={cat.id}
                to={`/c/${encodeURIComponent(cat.id)}/${slug}`}
                className="dc-card text-decoration-none"
              >
                <div className="dc-card-inner">
                  <div className="dc-card-thumb">
                    <img
                      src={img}
                      alt={label}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src =
                          'https://via.placeholder.com/160x160?text=Image'
                      }}
                    />
                  </div>
                </div>
                <div className="dc-card-label">{label}</div>
              </Link>
            )
          })}
        </div>
      </div>
    </section>
  )
}
